import React, { useState } from 'react';
import { 
  DocumentTextIcon,
  PaperClipIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AiCandidature from './AiCandidature';

const ApplicationForm = ({ project, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    message: '',
    contactInfo: {
      phone: '',
      address: '',
      website: '',
      linkedin: '',
      notes: ''
    },
    documents: [],
    additionalInfo: {
      experience: '',
      motivation: '',
      timeline: '',
      budget: ''
    }
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (formData.documents.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} fichiers autorisés`);
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(`Le fichier ${file.name} est trop volumineux (max 10MB)`);
        return false;
      }
      return true;
    });

    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, ...validFiles]
    }));
  };

  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.message.trim()) {
      newErrors.message = 'Le message de candidature est requis';
    } else if (formData.message.trim().length < 50) {
      newErrors.message = 'Le message doit contenir au moins 50 caractères';
    }

    if (!formData.contactInfo.phone.trim() && !formData.contactInfo.notes.trim()) {
      newErrors.contactInfo = 'Veuillez fournir au moins un moyen de contact';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    onSubmit(formData);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Informations sur l'appel à projet */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Candidature pour :</h3>
        <p className="text-blue-800 font-medium">{project.title}</p>
        <p className="text-blue-700 text-sm mt-1">
          Type : {project.type} • Budget : {project.budget} {project.currency}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {project.id && (
          <AiCandidature
            callId={project.id}
            formData={formData}
            onUse={(p) => setFormData((prev) => ({
              ...prev,
              message: p.message || prev.message,
              additionalInfo: {
                ...prev.additionalInfo,
                experience: p.experience || prev.additionalInfo.experience,
                motivation: p.motivation || prev.additionalInfo.motivation,
                timeline: p.timeline || prev.additionalInfo.timeline,
                budget: p.budget || prev.additionalInfo.budget,
              },
            }))}
          />
        )}

        {/* Message de candidature */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message de candidature *
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => handleChange('message', e.target.value)}
            rows={6}
            className={`w-full border rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.message ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Décrivez votre projet, votre motivation et pourquoi vous êtes le candidat idéal pour cet appel à projet..."
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-600">{errors.message}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            {formData.message.length}/500 caractères minimum
          </p>
        </div>

        {/* Informations de contact */}
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">Informations de contact</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone
              </label>
              <input
                type="tel"
                value={formData.contactInfo.phone}
                onChange={(e) => handleChange('contactInfo.phone', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="+33 1 23 45 67 89"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site web / Portfolio
              </label>
              <input
                type="url"
                value={formData.contactInfo.website}
                onChange={(e) => handleChange('contactInfo.website', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://votre-site.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LinkedIn
              </label>
              <input
                type="url"
                value={formData.contactInfo.linkedin}
                onChange={(e) => handleChange('contactInfo.linkedin', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://linkedin.com/in/votre-profil"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse
              </label>
              <input
                type="text"
                value={formData.contactInfo.address}
                onChange={(e) => handleChange('contactInfo.address', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Ville, Pays"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Informations complémentaires
            </label>
            <textarea
              value={formData.contactInfo.notes}
              onChange={(e) => handleChange('contactInfo.notes', e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Disponibilités, préférences de contact, informations importantes..."
            />
          </div>
          {errors.contactInfo && (
            <p className="mt-1 text-sm text-red-600">{errors.contactInfo}</p>
          )}
        </div>

        {/* Informations sur le projet */}
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">Informations sur votre projet</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Votre expérience dans ce domaine
              </label>
              <textarea
                value={formData.additionalInfo.experience}
                onChange={(e) => handleChange('additionalInfo.experience', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Décrivez votre expérience et vos compétences..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivation et objectifs
              </label>
              <textarea
                value={formData.additionalInfo.motivation}
                onChange={(e) => handleChange('additionalInfo.motivation', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Pourquoi ce projet vous intéresse-t-il ? Quels sont vos objectifs ?"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timeline prévue
                </label>
                <input
                  type="text"
                  value={formData.additionalInfo.timeline}
                  onChange={(e) => handleChange('additionalInfo.timeline', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="ex: 6-12 mois"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget estimé
                </label>
                <input
                  type="text"
                  value={formData.additionalInfo.budget}
                  onChange={(e) => handleChange('additionalInfo.budget', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="ex: 50 000 EUR"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Documents */}
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">Documents (optionnel)</h4>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <div className="space-y-2">
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                  <PaperClipIcon className="h-4 w-4 mr-2" />
                  Ajouter des documents
                </span>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              <p className="text-sm text-gray-500">
                PDF, DOC, DOCX, TXT, JPG, PNG (max 10MB par fichier, 5 fichiers max)
              </p>
            </div>
          </div>

          {/* Liste des documents */}
          {formData.documents.length > 0 && (
            <div className="mt-4 space-y-2">
              {formData.documents.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDocument(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Avertissement */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Important :</p>
              <ul className="mt-1 list-disc list-inside space-y-1">
                <li>Votre candidature sera transmise à l'organisateur de l'appel à projet</li>
                <li>Vous recevrez une notification par email lors de la réponse</li>
                <li>Vous pouvez suivre l'état de votre candidature dans "Mes Candidatures"</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Boutons */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Envoi en cours...' : 'Envoyer la candidature'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApplicationForm;
